import './styles.scss';

import { useState, useEffect, memo, useRef, useCallback } from 'react';

import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';

import Hls from 'hls.js';

import heroImage from '../../../assets/textures/squares.png';

import Button from '../../../components/button';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/icons/arrow-left.svg';

import AvatarsRow from '../../../components/avatarsRow';

import videoThumbnail from '../../../assets/draws/running-card.png';

// Usa caminho relativo para o arquivo m3u8 - o webpack pode não processar corretamente
// Se estiver em src/assets, o caminho será processado pelo webpack
// Se não funcionar, mova os arquivos para public/videos/running/ e use: `${process.env.PUBLIC_URL}/videos/running/1112.m3u8`
const video_running = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/running/saida.m3u8`
    : '/videos/running/saida.m3u8';
const video_gym = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/gym/saida.m3u8`
    : '/videos/gym/saida.m3u8';
const video_home = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/home/saida.m3u8`
    : '/videos/home/saida.m3u8';
const video_pilates = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/pilates/saida.m3u8`
    : '/videos/pilates/saida.m3u8';
const video_nutrition = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/nutrition/saida.m3u8`
    : '/videos/nutrition/saida.m3u8';
const video_yoga = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/yoga/saida.m3u8`
    : '/videos/yoga/saida.m3u8';
const video_meditation = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/videos/meditation/saida.m3u8`
    : '/videos/meditation/saida.m3u8';

function HandwritingText() {
    const words = ['fitness', 'wellness'];
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [displayedText, setDisplayedText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const currentWord = words[currentWordIndex];

        // Se a palavra está completa e não está apagando, espera antes de começar a apagar
        if (!isDeleting && displayedText === currentWord) {
            const pauseTimer = setTimeout(() => setIsDeleting(true), 2000);
            return () => clearTimeout(pauseTimer);
        }

        const typingSpeed = isDeleting ? 100 : 150;

        const timer = setTimeout(() => {
            if (!isDeleting) {
                // Digitando
                if (displayedText.length < currentWord.length) {
                    setDisplayedText(currentWord.substring(0, displayedText.length + 1));
                }
            } else {
                // Apagando
                if (displayedText.length > 0) {
                    setDisplayedText(currentWord.substring(0, displayedText.length - 1));
                } else {
                    // Palavra apagada, muda para a próxima
                    setIsDeleting(false);
                    setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
                }
            }
        }, typingSpeed);

        return () => clearTimeout(timer);
    }, [displayedText, isDeleting, currentWordIndex, words]);

    return (
        <h1 className="handwriting text-linear-animated">{displayedText || '\u00A0'}</h1>
    )
}

// Memoiza o componente para evitar re-renders desnecessários
const MemoizedHandwritingText = memo(HandwritingText);

export default function Hero() {

    return (
        <div className="hero">
            <img src={heroImage} alt="Hero Image" className="background" />

            <div className='content mt-2'>
                <AvatarsRow />

                <h1 style={{ lineHeight: '60px !important' }} className="mt-2">Construa um legado através da sua comunidade <MemoizedHandwritingText /></h1>

                <h4 className='pb-2 pt-1'>A única rede social fitness brasileira, onde você transforma conhecimento em receita recorrente</h4>

                <Button.Large
                    text="Criar minha comunidade agora"
                    onClick={window.config.redirects.openApp}
                    rightIcon={<ArrowLeftIcon />}
                />
            </div>

            <VideosCarousel />
        </div>
    );
}

// Lista de vídeos movida para fora do componente para evitar recriação
const videosList = [
    {
        id: 7,
        src: video_meditation,
        title: "Meditação"
    },

    {
        id: 2,
        src: video_gym,
        title: "Academia"
    },
    {
        id: 4,
        src: video_pilates,
        title: "Pilates"
    },

    {
        id: 5,
        src: video_nutrition,
        title: "Nutrição"
    },
    {
        id: 3,
        src: video_home,
        title: "Treinos em casa"
    },
    {
        id: 6,
        src: video_yoga,
        title: "Yoga"
    },
    {
        id: 1,
        src: video_running,
        title: "Corrida"
    },
];

const VideosCarousel = memo(function VideosCarousel() {
    const videos = videosList;
    const heroCarouselRef = useRef(null);
    const latestDetailRef = useRef(null);

    // Valores de scroll para animação suave
    const scrollTop = useMotionValue(0);
    const scrollTopSpring = useSpring(scrollTop, {
        stiffness: 100,
        damping: 30,
        mass: 0.5,
    });

    // Configurações de animação
    const scrollStart = 150;
    const scrollEnd = 700;
    const initialCarouselWidth = 116;
    const finalCarouselWidth = 160;
    const initialCarouselOpacity = 1;
    const finalCarouselOpacity = 0.3;
    const initialScale = 1;
    const finalScale = finalCarouselWidth / initialCarouselWidth;

    // Transformações suaves baseadas no scroll
    const carouselScale = useTransform(scrollTopSpring, (value) => {
        if (value < scrollStart) {
            return initialScale;
        } else if (value >= scrollEnd) {
            return finalScale;
        } else {
            const progress = (value - scrollStart) / (scrollEnd - scrollStart);
            return initialScale + (finalScale - initialScale) * progress;
        }
    });

    const carouselOpacity = useTransform(scrollTopSpring, (value) => {
        if (value < scrollStart) {
            return initialCarouselOpacity;
        } else if (value >= scrollEnd) {
            return finalCarouselOpacity;
        } else {
            const progress = (value - scrollStart) / (scrollEnd - scrollStart);
            return initialCarouselOpacity + (finalCarouselOpacity - initialCarouselOpacity) * progress;
        }
    });

    // Combina o translate3d do CSS com o scale do motion
    const carouselTransform = useTransform(
        carouselScale,
        (scale) => `translate3d(-50%, 50%, 0) scale(${scale})`
    );

    useEffect(() => {
        const handlePageScroll = (event) => {
            const detail = event.detail;
            if (!detail) return;

            latestDetailRef.current = detail;
            scrollTop.set(detail.scrollTop);
        };

        window.addEventListener('page-scroll', handlePageScroll);

        const initialDetail = window.__ginasteePageScrollState;
        if (initialDetail) {
            latestDetailRef.current = initialDetail;
            scrollTop.set(initialDetail.scrollTop);
        }

        return () => {
            window.removeEventListener('page-scroll', handlePageScroll);
        };
    }, [scrollTop]);

    const VideoCard = memo(function VideoCard({ video, opacity }) {
        const videoRef = useRef(null);
        const hlsRef = useRef(null);
        const isInitializedRef = useRef(false);
        const videoSrcRef = useRef(null);

        useEffect(() => {
            const videoElement = videoRef.current;
            if (!videoElement) return;

            // Se já foi inicializado e o vídeo está reproduzindo, não reinicializa
            if (isInitializedRef.current) {
                // Verifica se o vídeo ainda está carregado e reproduzindo
                if (videoElement.readyState >= 2 && !videoElement.paused) {
                    return; // Vídeo já está funcionando, não precisa reinicializar
                }
                // Se o vídeo parou, tenta apenas retomar a reprodução
                if (videoElement.readyState >= 2 && videoElement.paused) {
                    videoElement.play().catch(() => { });
                    return;
                }
            }

            let hls = null;
            // Garante que o caminho seja uma string válida
            let videoSrc = video.src;
            if (typeof video.src === 'object' && video.src !== null) {
                videoSrc = video.src.default || video.src;
            }
            videoSrc = String(videoSrc);

            // Se o src não mudou e já foi inicializado, não reinicializa
            if (isInitializedRef.current && videoSrcRef.current === videoSrc) {
                return;
            }

            videoSrcRef.current = videoSrc;

            console.log('Video source:', videoSrc);
            console.log('HLS available:', !!Hls);
            console.log('HLS supported:', Hls ? Hls.isSupported() : false);

            // Configurações do vídeo ANTES de carregar
            videoElement.loop = true;
            videoElement.muted = true;
            videoElement.playsInline = true;
            videoElement.setAttribute('playsinline', 'true');
            videoElement.setAttribute('webkit-playsinline', 'true');
            videoElement.setAttribute('autoplay', 'true');
            videoElement.setAttribute('muted', 'true');
            videoElement.setAttribute('loop', 'true');
            videoElement.setAttribute('preload', 'auto');

            // Função para tentar reproduzir o vídeo
            const attemptPlay = () => {
                if (videoElement.readyState >= 2 && videoElement.paused) {
                    const playPromise = videoElement.play();
                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                console.log('✅ Video playing successfully');
                            })
                            .catch(err => {
                                console.warn('⚠️ Autoplay prevented:', err);
                                // Tenta novamente após um pequeno delay
                                setTimeout(() => {
                                    videoElement.play().catch(e => {
                                        console.warn('⚠️ Second play attempt failed:', e);
                                    });
                                }, 500);
                            });
                    }
                }
            };

            // Verifica se o navegador suporta HLS nativamente (Safari)
            if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                // Se já tem src e está funcionando, não reinicializa
                if (videoElement.src && videoElement.src === videoSrc && videoElement.readyState >= 2) {
                    return;
                }

                console.log('🍎 Using native HLS support (Safari)');
                videoElement.src = videoSrc;

                const handleLoadedMetadata = () => attemptPlay();
                const handleCanPlay = () => attemptPlay();
                const handleLoadedData = () => attemptPlay();
                const handleCanPlayThrough = () => attemptPlay();

                videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
                videoElement.addEventListener('canplay', handleCanPlay);
                videoElement.addEventListener('loadeddata', handleLoadedData);
                videoElement.addEventListener('canplaythrough', handleCanPlayThrough);

                // Cleanup dos listeners
                return () => {
                    videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
                    videoElement.removeEventListener('canplay', handleCanPlay);
                    videoElement.removeEventListener('loadeddata', handleLoadedData);
                    videoElement.removeEventListener('canplaythrough', handleCanPlayThrough);
                };
            }
            // Usa hls.js para outros navegadores
            else if (Hls && Hls.isSupported()) {
                // Se já existe uma instância HLS funcionando, não recria
                if (hlsRef.current && hlsRef.current.media === videoElement) {
                    return;
                }

                console.log('📺 Using hls.js for HLS playback');
                try {
                    hls = new Hls({
                        enableWorker: true,
                        lowLatencyMode: false,
                        backBufferLength: 90, // Buffer maior para evitar travamentos
                        maxBufferLength: 30,
                        maxMaxBufferLength: 60,
                        maxBufferSize: 60 * 1000 * 1000, // 60MB
                        maxBufferHole: 0.5,
                        highBufferWatchdogPeriod: 2,
                        nudgeOffset: 0.1,
                        nudgeMaxRetry: 3,
                        maxFragLoadingTimeOut: 20000,
                        fragLoadingTimeOut: 20000,
                        manifestLoadingTimeOut: 10000,
                        levelLoadingTimeOut: 10000,
                    });

                    hls.loadSource(videoSrc);
                    hls.attachMedia(videoElement);

                    const handleManifestParsed = () => {
                        console.log('✅ HLS manifest parsed');
                        setTimeout(attemptPlay, 100);
                    };

                    const handleLevelLoaded = () => {
                        console.log('✅ HLS level loaded');
                        attemptPlay();
                    };

                    const handleFragLoaded = () => {
                        // Tenta reproduzir quando o primeiro fragmento carregar
                        if (videoElement.paused && videoElement.readyState >= 2) {
                            attemptPlay();
                        }
                    };

                    const handleError = (event, data) => {
                        console.error('❌ HLS Error:', data);
                        if (data.fatal) {
                            switch (data.type) {
                                case Hls.ErrorTypes.NETWORK_ERROR:
                                    console.warn('🔄 Fatal network error, trying to recover...');
                                    hls.startLoad();
                                    break;
                                case Hls.ErrorTypes.MEDIA_ERROR:
                                    console.warn('🔄 Fatal media error, trying to recover...');
                                    hls.recoverMediaError();
                                    break;
                                default:
                                    console.warn('💥 Fatal error, destroying HLS instance');
                                    hls.destroy();
                                    break;
                            }
                        }
                    };

                    hls.on(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
                    hls.on(Hls.Events.LEVEL_LOADED, handleLevelLoaded);
                    hls.on(Hls.Events.FRAG_LOADED, handleFragLoaded);
                    hls.on(Hls.Events.ERROR, handleError);

                    hlsRef.current = hls;

                    // Cleanup dos listeners HLS
                    return () => {
                        if (hls) {
                            hls.off(Hls.Events.MANIFEST_PARSED, handleManifestParsed);
                            hls.off(Hls.Events.LEVEL_LOADED, handleLevelLoaded);
                            hls.off(Hls.Events.FRAG_LOADED, handleFragLoaded);
                            hls.off(Hls.Events.ERROR, handleError);
                        }
                    };
                } catch (error) {
                    console.error('❌ Error initializing HLS:', error);
                }
            }
            // Fallback para navegadores sem suporte
            else {
                console.error('❌ HLS is not supported in this browser');
                console.log('Hls available:', !!Hls);
                console.log('Hls.isSupported:', Hls ? Hls.isSupported() : 'N/A');
            }

            // Marca como inicializado
            isInitializedRef.current = true;
        }, [video.src]);

        // Cleanup apenas quando o componente realmente desmontar
        useEffect(() => {
            return () => {
                if (hlsRef.current) {
                    hlsRef.current.destroy();
                    hlsRef.current = null;
                }
                isInitializedRef.current = false;
            };
        }, []);

        return (
            <div className="video-card">
                <motion.video
                    ref={videoRef}
                    loop
                    muted
                    playsInline
                    autoPlay
                    className="video-player"
                    style={{
                        opacity: opacity,
                    }}
                />
                <h5>{video.title}</h5>
            </div>
        )
    });

    return (
        <motion.div
            className="hero-videos-carousel"
            ref={heroCarouselRef}
            style={{
                transform: carouselTransform,
            }}
        >
            {videos.map((video) => (
                <VideoCard key={`video-carousel-${video.id}`} video={video} opacity={carouselOpacity} />
            ))}
        </motion.div>
    )
});